import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { rooms, chores } from '../data/mockData';
import { EnergyGauge, UsageLineChart } from '../components/Charts';

const Dashboard = () => {
  const urgentChores = chores.filter(c => c.priority === 'high' || c.priority === 'medium').slice(0, 2);

  return (
    <div className="p-6 space-y-6 h-full overflow-y-auto scrollbar-hide pb-24 md:pb-6">
      <header className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-100 tracking-tight">System Overview</h1>
          <p className="text-sm text-teal-400 font-mono mt-1">STATUS: NOMINAL // ALL SYSTEMS GO</p>
        </div>
        <div className="flex items-center space-x-3 bg-slate-800/80 px-4 py-2 rounded-full border border-slate-700">
          <SafeIcon icon="FiCloudDrizzle" className="text-slate-400" />
          <span className="text-sm font-medium">68°F Outside</span>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Environment Matrix */}
        <div className="col-span-1 md:col-span-2 glass-panel rounded-2xl p-5">
          <h2 className="text-lg font-semibold mb-4 flex items-center text-slate-200">
            <SafeIcon icon="FiGrid" className="mr-2 text-teal-500" /> Space Matrix
          </h2>
          <div className="grid grid-cols-2 gap-4">
            {rooms.map((room, i) => (
              <motion.div key={room.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                className={`p-4 rounded-xl border ${room.status === 'alert' ? 'border-amber-500/50 bg-amber-900/20' : 'border-slate-700 bg-slate-800/50'}`}>
                <div className="flex justify-between items-start mb-2">
                  <span className="font-medium text-slate-300">{room.name}</span>
                  <div className={`w-2 h-2 rounded-full ${room.status === 'optimal' ? 'bg-teal-400' : room.status === 'alert' ? 'bg-amber-400 animate-pulse' : 'bg-slate-400'}`} />
                </div>
                <div className="flex space-x-4 text-xs font-mono text-slate-400">
                  <span className="flex items-center"><SafeIcon icon="FiThermometer" className="mr-1" />{room.temp}</span>
                  <span className="flex items-center"><SafeIcon icon="FiDroplet" className="mr-1" />{room.humidity}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Energy Pulse */}
        <div className="glass-panel rounded-2xl p-5 flex flex-col items-center justify-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-teal-500 to-transparent opacity-50" />
          <h2 className="text-lg font-semibold w-full text-left mb-2 flex items-center">
            <SafeIcon icon="FiZap" className="mr-2 text-amber-400" /> Energy Pulse
          </h2>
          <EnergyGauge value={42} />
          <div className="mt-4 w-full">
            <UsageLineChart />
          </div>
        </div>
      </div>

      {/* Priority Queues */}
      <div className="glass-panel rounded-2xl p-5 border-l-4 border-l-amber-500">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <SafeIcon icon="FiAlertCircle" className="mr-2 text-amber-400" /> Priority Action Queue
        </h2>
        <div className="space-y-3">
          {urgentChores.map(chore => (
            <div key={chore.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center border border-amber-500/30">
                  <SafeIcon icon="FiTool" className="text-amber-400" />
                </div>
                <div>
                  <p className="font-medium text-slate-200">{chore.title}</p>
                  <p className="text-xs text-slate-400">{chore.room} • Due: {chore.due}</p>
                </div>
              </div>
              <button className="px-4 py-1.5 bg-teal-500/10 text-teal-400 border border-teal-500/30 rounded-md text-sm hover:bg-teal-500/20 transition-colors">
                Resolve
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;